import type { CareerAggregates } from "@dawn/adapters";
import { formatStagingPageTitle } from "@/shared/deployTarget";
import { clearSiteState, publishSiteState } from "@site/siteAdapter";
import { formatCareerTeaser } from "@site/config/careerCopy";
import { useLocale } from "@site/LocaleContext";
import {
  isReleaseUnseen,
  markReleaseSeen,
  registerReleaseNoticeSync,
  resolveLatestReleaseId,
} from "@site/config/releaseNotice";
import { useEffect, useLayoutEffect, useRef, useState, type RefObject } from "react";
import styles from "@site/pages/LandingPage.module.css";

interface LandingPageProps {
  landingVisible: boolean;
  isLaunching: boolean;
  career: CareerAggregates;
  playButtonRef: RefObject<HTMLButtonElement | null>;
  gearButtonRef: RefObject<HTMLButtonElement | null>;
  chronicleButtonRef: RefObject<HTMLButtonElement | null>;
  onPlay: () => void;
  onSettings: () => void;
  onChronicle: () => void;
}

export function LandingPage({
  landingVisible,
  isLaunching,
  career,
  playButtonRef,
  gearButtonRef,
  chronicleButtonRef,
  onPlay,
  onSettings,
  onChronicle,
}: LandingPageProps) {
  const { locale, strings } = useLocale();
  const [showReleaseNotice, setShowReleaseNotice] = useState(false);
  const latestReleaseIdRef = useRef<string | null>(null);

  useLayoutEffect(() => {
    if (!landingVisible) {
      clearSiteState();
      return;
    }

    publishSiteState(locale, strings.landing.playLabel);
  }, [landingVisible, locale, strings.landing.playLabel]);

  useEffect(() => {
    document.title = formatStagingPageTitle(strings.gameTitle);
  }, [strings.gameTitle]);

  useEffect(() => {
    const syncFromStorage = (): void => {
      const releaseId = latestReleaseIdRef.current;
      if (!releaseId) {
        setShowReleaseNotice(false);
        return;
      }
      setShowReleaseNotice(isReleaseUnseen(releaseId));
    };

    void (async () => {
      const releaseId = await resolveLatestReleaseId();
      latestReleaseIdRef.current = releaseId;
      syncFromStorage();
    })();

    const unsubscribe = registerReleaseNoticeSync(syncFromStorage);
    window.addEventListener("pageshow", syncFromStorage);
    return () => {
      unsubscribe();
      window.removeEventListener("pageshow", syncFromStorage);
    };
  }, []);

  const handleReleaseNoticeClick = (): void => {
    const releaseId = latestReleaseIdRef.current;
    if (!releaseId) {
      return;
    }
    markReleaseSeen(releaseId);
  };

  const showCareer = career.totalWins > 0;
  const playLabel = isLaunching
    ? strings.landing.loadingLabel
    : strings.landing.playLabel;

  return (
    <main id="landing" className={styles.page} hidden={!landingVisible}>
      <div className={`site-page__content ${styles.body}`}>
        <div className={styles.header}>
          <h1 className={`site-title ${styles.title}`}>{strings.gameTitle}</h1>
          <button
            ref={gearButtonRef}
            className={styles.settingsGear}
            type="button"
            data-testid="landing-settings-gear"
            aria-haspopup="dialog"
            aria-label={strings.landing.settingsGearAriaLabel}
            onClick={onSettings}
          >
            <span className={styles.settingsGearIcon} aria-hidden="true">
              ⚙
            </span>
          </button>
        </div>
        <p className={styles.earlyAccessNotice} data-testid="early-access-notice">
          <span className={styles.earlyAccessBadge}>
            {strings.landing.earlyAccessLabel}
          </span>
          <span className={styles.earlyAccessText}>
            {strings.landing.earlyAccessNotice}
          </span>
        </p>
        <div className={styles.copy}>
          <p className={styles.description}>{strings.landing.primaryDescription}</p>
          <p className={styles.descriptionSecondary}>
            {strings.landing.secondaryDescription}
          </p>
          <a
            className={`site-link ${styles.learnMore}`}
            href="/about.html"
            data-testid="learn-more-link"
          >
            {strings.landing.learnMoreLabel}
          </a>
        </div>
        <div className={styles.actions}>
          <div
            className={styles.career}
            data-testid="career-teaser"
            hidden={!showCareer}
          >
            <p className={styles.careerSummary} data-testid="career-teaser-summary">
              {showCareer
                ? formatCareerTeaser(strings.landing.careerTeaserFormat, career)
                : ""}
            </p>
            <button
              ref={chronicleButtonRef}
              className={`site-link ${styles.careerChronicleLink}`}
              type="button"
              data-testid="career-view-chronicle"
              onClick={onChronicle}
            >
              {strings.landing.careerViewChronicleLabel}
            </button>
          </div>
          <div className={styles.playWrap}>
            <button
              ref={playButtonRef}
              className={styles.playButton}
              type="button"
              data-testid="play-button"
              disabled={isLaunching}
              onClick={onPlay}
            >
              {playLabel}
            </button>
            <a
              className={`site-link ${styles.whatsNew}`}
              href="/changelog.html"
              data-testid="release-notice"
              hidden={!showReleaseNotice}
              onClick={handleReleaseNoticeClick}
            >
              {strings.landing.releaseNoticeText}
            </a>
          </div>
        </div>
      </div>
    </main>
  );
}
